import Colors from "@/p5/colors.js";
import SrcRenderer from "@/p5/SrcRenderer.js";
import TimeManager from "@/p5/TimeManager.js";
import { debounce } from "lodash";

/**
 * Default configuration for this sketch. Individual settings can be overridden when calling the createSketch method.
 */
const defaultConfig = {
  parentId: "sketch-container",
  shader: {
    vertFile: "/shader.vert",
    fragFile: "/shader.frag",
    fontFile: "/fonts/TWKEverettMono-Regular-web.ttf",
    staticDisplacementScale: null,
    displacementYMapOffset: 0.0,
    displacementYMapScale: 1.0,
    gridSizeFn: (width, height) => Math.floor(height / 19),
    video: "/homepage_ascii_video.mp4",
    videoStartMillis: 3000,
  },
  renderer: {
    enableMouse: false,
    colors: [Colors.sub, Colors.mint, Colors.mint],
    staticPositionOffset: null,
    squareScaleLarge: 1 / 3,
  },
  timer: {
    enableSquare: true,
    lengthSimpleMillis: 10000,
    lengthChaosMillis: 28000,
  },
  shimmer: {
    enableShimmer: true,
    shimmerColor: Colors.mint,
    shimmerStartMillis: 25000,
  },
};

const createSketch = (userConfig, onLoaded) => {
  const renderer = Object.assign({}, defaultConfig.renderer, userConfig.renderer || {});
  const shader = Object.assign({}, defaultConfig.shader, userConfig.shader || {});
  const timer = Object.assign({}, defaultConfig.timer, userConfig.timer || {});
  const shimmer = Object.assign({}, defaultConfig.shimmer, userConfig.shimmer || {});
  const config = {
    parentId: userConfig.parentId || defaultConfig.parentId,
    renderer,
    shader,
    timer,
    shimmer,
  };

  let sketchPaused;

  return [
    (p) => {
      // Canvas basics
      let canvas;
      let canvasRatio;

      let font;

      // Time management
      let lastTimeMillis = 0;
      let time = 0;

      // Main resources for video-based shader magic
      let charset = [" ", ":", "-", "+", "a", "w", "z", "n", "c", "e"];
      let glyphIndexes = [0.0, 1.0, 2.0, 3.0, 4.0, 5.0, 6.0, 7.0, 8.0, 9.0];

      let vid;
      let src;
      let timer;

      let asciiGridImg;
      let asciiShader2;

      let tilesetPNG;

      p.preload = () => {
        font = p.loadFont(config.shader.fontFile);

        tilesetPNG = p.loadImage("/ascii-tileset.png");

        // Load two instances of the same shader so we can do a two-pass filter
        asciiShader2 = p.loadShader(config.shader.vertFile, config.shader.fragFile);
      };

      p.windowResized = debounce(() => {
        const div = document.getElementById(config.parentId);
        if (!div) {
          // div may have been removed as part of a next.js page transition — return silently.
          return;
        }

        p.resizeCanvas(div.offsetWidth, div.clientHeight);
        vid?.size(p.width, p.height);
        canvasRatio = p.width / p.height;
      }, 300);

      p.setup = () => {
        let seed = p.random(999999999);
        // console.log({ seed });
        p.noiseSeed(seed);

        const div = document.getElementById(config.parentId);
        canvas = p.createCanvas(div.offsetWidth, div.clientHeight, p.WEBGL);
        // p.pixelDensity(2);

        canvasRatio = p.width / p.height;
        // canvas.parent(div);
        p.frameRate(20);

        vid = p.createVideo(config.shader.video, () => {
          vid.time(config.shader.videoStartMillis / 1000);
          vid.speed(0.5);
          vid.loop();
          vid.size(p.width, p.height);
          // vid.hide();
          onLoaded && onLoaded();
          vid.elt.playsInline = true;
          vid.elt.muted = true;
          vid.elt.autoplay = true;
        });
        vid.elt.playsInline = true;
        vid.elt.muted = true;
        vid.elt.autoplay = true;

        setupAsciiTileset();
        timer = new TimeManager(
          p,
          config.timer.enableSquare,
          config.timer.lengthSimpleMillis,
          config.timer.lengthChaosMillis,
          config.renderer.squareScaleLarge,
          config.shader.videoStartMillis
        );
        src = new SrcRenderer(
          p,
          timer,
          config.renderer.staticPositionOffset,
          config.shimmer.enableShimmer,
          config.shimmer.shimmerStartMillis,
          config.renderer.colors,
          config.shimmer.shimmerColor
        );
      };

      const setupAsciiTileset = () => {
        p.textFont(font);

        let asciiScale = 1;
        p.textSize(32 * asciiScale);

        asciiGridImg = p.createGraphics(
          (p.textWidth("a") + 10 * asciiScale) * 10, //
          (p.textWidth("a") + 10 * asciiScale) * 10
        );

        asciiGridImg.background("black");
        asciiGridImg.fill("white");

        asciiGridImg.image(
          tilesetPNG,
          0,
          0,
          10 * (p.textWidth("a") + 10 * asciiScale),
          10 * (p.textWidth("a") + 10 * asciiScale)
        );

        // let i = 0;
        // for (let x = 0; x < asciiGridImg.width; x += asciiGridImg.width / 10) {
        //   asciiGridImg.textFont(font);
        //   asciiGridImg.textSize(32 * asciiScale);
        //   asciiGridImg.text(charset[i], x, 20 * asciiScale);
        //   i++;
        // }
      };

      p.draw = () => {
        if (sketchPaused) {
          return;
        }
        // Time management
        const updateSrcTarget = timer.updateChaos(vid);

        if (updateSrcTarget && timer.targetChaos === 0.0) {
          src.tmx = p.random(p.width / 2 - 200, p.width / 2 + 200);
          src.tmy = p.random(p.height / 2 - 200, p.height / 2 + 200);
        } else if (updateSrcTarget && timer.targetChaos === 1.0) {
          src.tmx = p.width / 2;
          src.tmy = p.height / 2;
        }

        time += (p.millis() - lastTimeMillis) / 1000;
        lastTimeMillis = p.millis();

        src.render(config.renderer.enableMouse, p.mouseX !== 0 || p.mouseY !== 0);

        // Render ASCII-fied image based on pixelated image
        asciiShader2.setUniform("cam", src.layer);
        asciiShader2.setUniform("disp", vid);
        asciiShader2.setUniform("font", asciiGridImg);
        asciiShader2.setUniform("canvasRatio", canvasRatio);
        asciiShader2.setUniform("gridSize", config.shader.gridSizeFn(p.width, p.height));
        asciiShader2.setUniform("charOffsetScale", timer.charOffsetScale());
        asciiShader2.setUniform("time", time);
        asciiShader2.setUniform("glyphs", glyphIndexes);
        asciiShader2.setUniform(
          "dispScale",
          config.shader.staticDisplacementScale === null
            ? timer.displacementScale()
            : config.shader.staticDisplacementScale
        );
        asciiShader2.setUniform("displacementYMapOffset", config.shader.displacementYMapOffset);
        asciiShader2.setUniform("displacementYMapScale", config.shader.displacementYMapScale);

        p.noStroke();
        p.shader(asciiShader2);
        p.background(userConfig.colors?.bg ?? Colors.bg);
        p.rect(-p.width / 2, -p.height / 2, p.width, p.height);
        p.resetShader();
      };
    },
    (paused) => (sketchPaused = paused),
  ];
};

export default createSketch;
